import { useEffect, useState } from "react";
import {
  CreateTenantRequest,
  Tenant,
  UpdateTenantRequest,
} from "../../types/tenant";
import tenantRepository from "../../services/api/tenantRepository";
import {
  Container,
  Box,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Button,
  Snackbar,
  Alert,
  Tooltip,
} from "@mui/material";
import { Add, DeleteOutlined, EditOutlined } from "@mui/icons-material";
import CreateTenantDialog from "./modals/CreateTenantDialog";
import EditTenantDialog from "./modals/EditTenantDialog";
import Header from "../Header";
import DeleteConfirmationDialog from "./modals/DeleteConfirmationDialog";
import { AxiosError } from "axios";

const TenantsPage = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | undefined>();
  const [deletingTenant, setDeletingTenant] = useState<Tenant | undefined>();
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchTenants = async () => {
    try {
      const tenants = await tenantRepository.getAllTenants();
      setTenants(tenants);
    } catch (error) {
      console.error("Error fetching tenants:", error);
    }
  };
  useEffect(() => {
    if (tenants.length === 0) {
      fetchTenants();
    }
  }, [tenants]);

  const handleTenantCreate = async (tenant: CreateTenantRequest) => {
    try {
      await tenantRepository.createTenant(tenant);
      setIsOpen(false);
      fetchTenants();
      handleSuccessSnackbar("Tenant created successfully");
    } catch (error) {
      const axiosError = error as AxiosError;
      setIsSnackbarOpen(true);
      setErrorMessage("Error creating tenant: " + axiosError.message);
    }
  };

  const handleTenantUpdate = async (
    tenantId: string,
    tenant: UpdateTenantRequest
  ) => {
    try {
      const updatingTenant = { ...tenant };
      if (tenant.hdmCredentials?.username === "" && tenant.hdmCredentials?.password === "") {
        updatingTenant.hdmCredentials = undefined;
      }

      await tenantRepository.updateTenant(tenantId, updatingTenant);
      setEditingTenant(undefined);
      fetchTenants();
      handleSuccessSnackbar("Tenant updated successfully");
    } catch (error) {
      const axiosError = error as AxiosError;
      setIsSnackbarOpen(true);
      setErrorMessage("Error updating tenant: " + axiosError.message);
    }
  };

  const handleTenantDelete = async (tenantId: string) => {
    try {
      await tenantRepository.deleteTenant(tenantId);
      setDeletingTenant(undefined);
      fetchTenants();
      handleSuccessSnackbar("Tenant deleted successfully");
    } catch (error) {
      const axiosError = error as AxiosError;
      setIsSnackbarOpen(true);
      setErrorMessage("Error deleting tenant: " + axiosError.message);
    }
  };

  const handleSuccessSnackbar = (message: string) => {
    setSuccessMessage(message);
    setIsSnackbarOpen(true);
  };

  const handleCloseSnackbar = () => {
    setIsSnackbarOpen(false);
    setErrorMessage(null);
    setSuccessMessage(null);
  };

  return (
    <>
      <Header />
      {(errorMessage || successMessage) && (
        <Snackbar
          open={isSnackbarOpen}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
        >
          <Alert
            severity={errorMessage ? "error" : "success"}
            onClose={handleCloseSnackbar}
            sx={{ width: "100%" }}
          >
            {errorMessage || successMessage}
          </Alert>
        </Snackbar>
      )}
       <Container>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h2" component="h2">
            Tenants
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() => setIsOpen(true)}
            sx={{
              paddingLeft: 3,
              paddingRight: 4,
              textTransform: "none",
              backgroundColor: "#FFFFFF",
            }}
          >
            <Typography variant="body1">Add tenant</Typography>
          </Button>
        </Box>

        <TableContainer
          component={Paper}
          sx={{
            boxShadow: "none",
            border: "none",
            borderRadius: 2,
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Tenant Id</TableCell>
                <TableCell>Environment Id</TableCell>
                <TableCell>Archive Id</TableCell>
                <TableCell>HDM Api Endpoint</TableCell>
                <TableCell>Operations</TableCell>
                <TableCell width={24} />
                <TableCell width={24} />
              </TableRow>
            </TableHead>
            <TableBody>
              {tenants.map((tenant: Tenant, index: number) => (
                <TableRow key={index}>
                  <TableCell>{tenant.name}</TableCell>
                  <TableCell>{tenant.tenantId}</TableCell>
                  <TableCell>{tenant.environmentId}</TableCell>
                  <TableCell>{tenant.archiveId}</TableCell>
                  <TableCell>{tenant.hdmApiEndpoint}</TableCell>
                  <TableCell>{tenant.operations.join(", ")}</TableCell>
                  <TableCell>
                    <Tooltip title="Edit tenant" placement="top" arrow>
                      <IconButton className="row-button" color="inherit">
                        <EditOutlined
                          sx={{ color: "rgb(102, 84, 196)" }}
                          onClick={() => setEditingTenant(tenant)}
                        />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Delete tenant" placement="top" arrow>
                      <IconButton className="row-button" color="inherit">
                        <DeleteOutlined
                          sx={{ color: "rgb(102, 84, 196)" }}
                          onClick={() => setDeletingTenant(tenant)}
                        />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
      <CreateTenantDialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onSave={handleTenantCreate}
      />
      <EditTenantDialog
        onClose={() => setEditingTenant(undefined)}
        onSave={handleTenantUpdate}
        tenant={editingTenant}
      />
      <DeleteConfirmationDialog
        open={!!deletingTenant}
        onClose={() => setDeletingTenant(undefined)}
        onConfirm={handleTenantDelete}
        tenant={deletingTenant}
      />
    </>
  );
};

export default TenantsPage;
